import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FilterMatchMode } from 'primereact/api';
import {
    selectFilteredLocations,
    selectLocations,
    selectClients,
    selectAllBranchs,
    selectLoading,
    selectError,
    fetchLocationData
} from '../store/slices/locationSlice';
import { saveLocation } from '../utilities/api';
import Locationmodal from '../common/Locationmodal';
import './CategoryMaster.css';

const emptyLocation = {
    location_id: null,
    location_name: '',
    branch_id: null,
    client_id: null
};

const LocationContent = () => {
    const dispatch = useDispatch();
    const toast = useRef(null);

    const filteredLocations = useSelector(selectFilteredLocations);
    const locations = useSelector(selectLocations);
    const clients = useSelector(selectClients);
    const allBranchs = useSelector(selectAllBranchs);
    const loading = useSelector(selectLoading);
    const error = useSelector(selectError);

    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    const [displayDialog, setDisplayDialog] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(emptyLocation);

    useEffect(() => {
        if (error) {
            toast.current?.show({ severity: 'error', summary: 'Load Error', detail: error, life: 5000 });
        }
    }, [error]);

    const reload = () => {
        dispatch(
            fetchLocationData({
                rolename: localStorage.getItem('role') || '',
                clientname: localStorage.getItem('clientid') || ''
            })
        );
    };

    const handleSaveRequest = (savedLocation) => {
        const isEdit = !!savedLocation.location_id;
        confirmDialog({
            message: isEdit
                ? `Are you sure you want to update "${savedLocation.location_name}"?`
                : `Are you sure you want to add "${savedLocation.location_name}"?`,
            header: isEdit ? 'Confirm Update' : 'Confirm Add',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, Save',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-success',
            rejectClassName: 'p-button-text',
            accept: () => handleConfirmedSave(savedLocation)
        });
    };

    const handleConfirmedSave = async (savedLocation) => {
        try {
            const payload = { ...savedLocation, location_id: savedLocation.location_id || null };
            const resData = await saveLocation(payload);
            if (resData?.status) {
                toast.current.show({ severity: 'success', summary: 'Success', detail: resData.message || 'Saved successfully', life: 3000 });
                setDisplayDialog(false);
                setSelectedLocation(emptyLocation);
                reload();
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: resData?.message || 'Save failed. Please try again.', life: 4000 });
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.message || 'Save failed. Please try again.', life: 4000 });
        }
    };

    const actionBodyTemplate = (rowData) => (
        <Button
            icon="pi pi-pencil"
            rounded
            text
            className="p-button-warning"
            onClick={() => {
                setSelectedLocation({ ...rowData });
                setDisplayDialog(true);
            }}
            tooltip="Edit"
            tooltipOptions={{ position: 'top' }}
        />
    );

    return (
        <Card className="bill-reports-container">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                <div className="flex align-items-center gap-3 flex-wrap">
                    <h3 className="text-xl font-semibold m-0">Location List</h3>
                    <span style={{
                        background: '#f1f5f9', borderRadius: '20px', padding: '0.2rem 0.85rem',
                        fontSize: '0.8rem', fontWeight: '600', color: '#475569', border: '1px solid #e2e8f0'
                    }}>
                        <i className="pi pi-map-marker mr-1" style={{ fontSize: '0.75rem' }} />
                        {locations.length} Total
                    </span>
                    <span style={{
                        background: '#eff6ff', borderRadius: '20px', padding: '0.2rem 0.85rem',
                        fontSize: '0.8rem', fontWeight: '600', color: '#3b82f6', border: '1px solid #bfdbfe'
                    }}>
                        <i className="pi pi-filter mr-1" style={{ fontSize: '0.75rem' }} />
                        {filteredLocations.length} Showing
                    </span>
                </div>

                <div className="flex flex-wrap gap-2 align-items-center">
                    <span className="p-input-icon-left" style={{ minWidth: '220px' }}>
                        <i className="pi pi-search" />
                        <InputText
                            value={globalFilterValue}
                            onChange={(e) => {
                                setGlobalFilterValue(e.target.value);
                                setFilters({ global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS } });
                            }}
                            placeholder="Search locations..."
                            className="w-full"
                        />
                    </span>
                    <Button
                        label="Add Location"
                        icon="pi pi-plus"
                        className="p-button-primary"
                        onClick={() => {
                            setSelectedLocation(emptyLocation);
                            setDisplayDialog(true);
                        }}
                    />
                </div>
            </div>

            {loading ? (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <ProgressSpinner />
                </div>
            ) : (
                <div className="sub-masters-table-wrap">
                <DataTable
                    value={filteredLocations}
                    filters={filters}
                    globalFilterFields={['location_name', 'client_name', 'branch_name']}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                    currentPageReportTemplate="{first} to {last} of {totalRecords} records"
                    showGridlines
                    tableStyle={{ minWidth: '40rem' }}
                    className="p-datatable-sm sub-masters-table"
                    emptyMessage="No locations found"
                >
                    <Column field="location_id" header="ID" sortable style={{ width: '80px' }} />
                    <Column field="location_name" header="Location Name" sortable />
                    <Column field="client_name" header="Client" sortable style={{ width: '200px' }} />
                    <Column field="branch_name" header="Branch" sortable style={{ width: '200px' }} />
                    <Column header="Actions" body={actionBodyTemplate} style={{ width: '90px', textAlign: 'center' }} />
                </DataTable>
                </div>
            )}

            <Locationmodal
                visible={displayDialog}
                onHide={() => {
                    setDisplayDialog(false);
                    setSelectedLocation(emptyLocation);
                }}
                companyData={selectedLocation}
                onSave={handleSaveRequest}
                clients={clients}
                allBranchs={allBranchs}
            />
        </Card>
    );
};

export default LocationContent;
